import { segmentGroupAnatomy } from '@chakra-ui/react/anatomy';
import { defineSlotRecipe } from '@chakra-ui/react/styled-system';

const segmentGroupSlotRecipe = defineSlotRecipe({
  slots: segmentGroupAnatomy.keys(),
  className: 'segment-group',
  base: {
    root: {
      alignItems: 'center',
      backgroundColor: 'background/secondary',
      borderColor: 'border/minimal',
      borderRadius: '4',
      borderStyle: 'solid',
      borderWidth: '1',
      display: 'inline-flex',
      gap: '1',
      isolation: 'isolate',
      position: 'relative',
    },
    // Zag.js positions the indicator via inline CSS variables (--width, --height, --top, --left).
    // We must reference them here because our custom recipe replaces the Chakra default entirely.
    indicator: {
      backgroundColor: 'background/primary',
      borderRadius: '4',
      boxShadow: 'small',
      height: 'var(--height)',
      left: 'var(--left)',
      outline: '1px solid',
      outlineColor: 'border/regular',
      position: 'absolute',
      top: 'var(--top)',
      width: 'var(--width)',
      zIndex: -1,
    },
    item: {
      alignItems: 'center',
      borderRadius: '4',
      color: 'text/secondary',
      cursor: 'pointer',
      _icon: {
        color: 'icon/secondary',
      },
      display: 'flex',
      gap: '4',
      justifyContent: 'center',
      paddingInline: '12',
      position: 'relative',
      textStyle: 'body/md/regular',
      // Vertical separator between items
      _before: {
        backgroundColor: 'border/minimal',
        content: '""',
        insetBlock: '8',
        insetInlineStart: '-1px',
        position: 'absolute',
        width: '1',
      },
      // Hide separator on first item, and on both sides of the checked item
      '& + &[data-state=checked], &[data-state=checked] + &, &:first-of-type': {
        _before: { opacity: '0' },
      },
      // Focus ring on item when its hidden input is focused (same as Chakra default)
      '&:has(input:focus-visible)': {
        focusRing: 'outside',
      },
      _hover: {
        backgroundColor: 'background/hover',
        color: 'text/primary',
        _active: {
          backgroundColor: 'background/active',
        },
      },
      _active: {
        backgroundColor: 'background/active',
        color: 'text/primary',
      },
      _checked: {
        backgroundColor: 'transparent',
        color: 'text/primary',
        _active: {
          backgroundColor: 'transparent',
        },
        _hover: {
          backgroundColor: 'transparent',
          _active: {
            backgroundColor: 'transparent',
          },
        },
      },
      _disabled: {
        color: 'text/disabled',
        cursor: 'not-allowed',
        _hover: {
          backgroundColor: 'transparent',
        },
      },
    },
    itemText: {
      paddingInline: '4',
    },
  },
  variants: {
    fullWidth: {
      true: {
        root: {
          display: 'flex',
          width: '100%',
        },
        item: {
          flex: '1',
        },
      },
    },
    size: {
      lg: {
        item: {
          paddingBlock: '12',
          textStyle: 'body/lg/regular',
        },
      },
      md: {
        item: {
          paddingBlock: '8',
        },
      },
      sm: {
        item: {
          paddingBlock: '6',
        },
      },
    },
  },
  defaultVariants: {
    size: 'sm',
  },
});

export default segmentGroupSlotRecipe;
